import { getQuerySelector } from '@redux-requests/core';
import { createSelector } from '@reduxjs/toolkit';
import { Difference, DifferenceTypeNames } from 'types';
import { isCriticalContribution } from 'components/DifferenceExpansionPanel/barcodeMetadataGrouping';
import { fetchDifferences } from './actions';

export const getDifferences = createSelector([getQuerySelector({ type: fetchDifferences })], (state): Difference[] => {
  const differences = state.data ? state.data : [];
  return differences as Difference[];
});

export const getCommentedDifferences = createSelector([getDifferences], (differences) => {
  return differences.filter((difference) => {
    return difference.comment;
  });
});

export const getDifferenceGroups = createSelector([getDifferences], (differences): Difference[] => {
  const alreadyGrouped: Array<string> = [];
  return differences.filter((difference) => {
    if (difference.groupId) {
      // we need to keep track of the groups we already included in the differences list.
      if (alreadyGrouped.includes(difference.groupId)) {
        return false;
      }
      alreadyGrouped.push(difference.groupId);
    }
    return true;
  });
});

export const getDifferenceViewed = (differenceId: string) =>
  createSelector(
    [getDifferences],
    (differences) => differences.find((diff) => diff.id === differenceId)?.viewed || false,
  );

/**
 * Returns true when the inspection has graded barcode differences (those with a `grade`
 * metadata key) but none carry ISO/NonISO classification from CTF's gradeImpact field.
 * Ungraded barcode types (Code 11, Telepen, etc.) are excluded — CTF never emits gradeImpact
 * for them, so they should not trigger the warning regardless of inspection age.
 */
export const getBarcodeIsOutdatedByGradeImpact = createSelector([getDifferences], (differences): boolean => {
  const barcodeDiffs = differences.filter((d) => d.type === DifferenceTypeNames.Barcode);
  if (barcodeDiffs.length === 0) return false;
  // Ungraded barcode types never receive gradeImpact from CTF; only check graded ones.
  const gradedBarcodeDiffs = barcodeDiffs.filter((d) =>
    d.barcodeDetails?.barcodeMetadata?.some((item) => item.key === 'grade'),
  );
  if (gradedBarcodeDiffs.length === 0) return false;
  const isClassified = gradedBarcodeDiffs.some((d) =>
    d.barcodeDetails?.barcodeMetadata?.some((item) => isCriticalContribution(item.contribution)),
  );
  return !isClassified;
});
